import { useCallback, useRef } from 'react';

// Infinite scroll-UP for a chat transcript.
//
// The whole difficulty is that prepending messages moves everything down: the
// browser keeps `scrollTop` where it was, so the reader is thrown to a
// different part of the conversation the instant older messages arrive. The fix
// is to record `scrollHeight` before the prepend and add the delta back
// afterwards, in a layout-synchronous callback so it happens before paint.
//
// Also guards the common double-fire: a scroll event fires many times per
// gesture, and without an in-flight flag the same page is requested repeatedly.
export default function useScrollBackLoader({ hasMore, loadOlder }) {
  const scrollRef = useRef(null);
  const loadingRef = useRef(false);
  const anchorRef = useRef(null);

  const onScroll = useCallback(async () => {
    const el = scrollRef.current;
    if (!el || loadingRef.current || !hasMore) return;
    // 120px of runway, so the fetch starts just before the reader hits the top
    // rather than after they've already stopped.
    if (el.scrollTop > 120) return;

    loadingRef.current = true;
    anchorRef.current = el.scrollHeight - el.scrollTop;
    try {
      await loadOlder();
    } finally {
      // Restore the reading position on the next frame, once the prepended
      // rows have been laid out.
      requestAnimationFrame(() => {
        const node = scrollRef.current;
        if (node && anchorRef.current != null) {
          node.scrollTop = node.scrollHeight - anchorRef.current;
        }
        anchorRef.current = null;
        loadingRef.current = false;
      });
    }
  }, [hasMore, loadOlder]);

  // Whether a fetch is in flight, for the "loading older…" strip.
  const isLoadingOlder = () => loadingRef.current;

  return { scrollRef, onScroll, isLoadingOlder };
}
